/**
 * Lo que las pantallas de control de configuración muestran igual.
 *
 * Grabadores, paneles y cámaras enseñan lo mismo con datos distintos: unas
 * tarjetas de totales, un estado de estabilidad, una lista de desvíos y el
 * reparto del golden. Vive aquí para que las tres digan lo mismo con las
 * mismas palabras — y sobre todo para que una corrección no se aplique en una
 * sola.
 */
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { GrabadorCampoGolden, GrabadorDesvio } from "@/api/types";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

// Los tres estados que antes se veían como dos.
export const ESTABILIDAD: Record<
  string,
  {
    texto: string;
    variant: "destructive" | "secondary" | "outline";
    ayuda: string;
  }
> = {
  revirtio: {
    texto: "revirtió",
    variant: "destructive",
    ayuda:
      "estuvo en golden y volvió atrás solo — no le falta configurarlo, le falta que dejen de reescribírselo",
  },
  nunca_bien: {
    texto: "nunca bien",
    variant: "secondary",
    ayuda: "ninguna lectura lo ha tenido en golden",
  },
  aguanta: {
    texto: "aguanta",
    variant: "outline",
    ayuda: "en golden en ésta y en la lectura anterior",
  },
  bien_reciente: {
    texto: "bien (1 lectura)",
    variant: "outline",
    ayuda:
      "en golden, pero es la primera lectura que lo dice: una sola no prueba que aguante",
  },
  sin_lectura: {
    texto: "—",
    variant: "outline",
    ayuda: "nunca se ha podido leer",
  },
  sin_golden: {
    texto: "sin golden",
    variant: "outline",
    ayuda: "su agencia no está en el mapa de husos",
  },
};

export const ORIGEN: Record<string, string> = {
  agencia: "mapa de husos",
  politica: "política",
  mayoria: "mayoría",
  fijado_a_mano: "fijado a mano",
};

export function Tarjeta({
  titulo,
  valor,
  nota,
  alerta,
}: {
  titulo: string;
  valor: number | string;
  nota?: string;
  alerta?: boolean;
}) {
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="text-xs text-muted-foreground">{titulo}</div>
      <div
        className={cn("text-2xl font-semibold", alerta && "text-destructive")}
      >
        {valor}
      </div>
      {nota ? (
        <div className="text-xs text-muted-foreground">{nota}</div>
      ) : null}
    </div>
  );
}

/**
 * Los seis números que toda pantalla de control de configuración dice igual.
 *
 * Nace de homologar Grabadores, Paneles, Cámaras, Biométricos, HikCentral y
 * RUT: antes cada una contaba a su manera y no se podían comparar. El orden es
 * deliberado y no se cambia:
 *
 * 1. **Cobertura primera.** Un 100 % de cumplimiento sobre un tercio de la
 *    flota no es un 100 %. Mezclarlo fue el error de los 37 rojos falsos del
 *    dashboard de Holanda. Va primera para que nadie lea el cumplimiento sin
 *    ver antes sobre cuántos.
 * 2. **Sin leer** no cuenta ni como cumple ni como fuera: es "no sabemos".
 * 3. **No responden** es distinto de sin leer: ahí SÍ lo intentamos y el
 *    equipo no contestó. Se rotula por lo que se mide —no responde—, NO por la
 *    causa: un `offline` puede ser una caída real, pero también un bloqueo por
 *    intentos que nos hicimos nosotros al sondear con demasiada concurrencia
 *    (medido el 8-sep: 15 equipos pasaron `ok→offline` a la vez y volvieron
 *    solos). "Caído" sería una conclusión sobre la causa, y a veces es falsa.
 */
export type ResumenTotales = {
  /** Total de equipos del carril (cámaras, grabadores, paneles…). */
  total: number;
  /** Cuántos se pudieron leer. */
  leidos: number;
  /** Redondeado, 0-100. */
  cobertura_pct: number;
  cumplen: number;
  /** Estuvieron en golden y volvieron atrás solos. */
  revirtieron: number;
  fuera: number;
  /** Desvíos en campos críticos, para la nota de "Fuera de golden". */
  criticos: number;
  /** Leídos que no son cumple ni fuera, o aún sin cosechar. No sabemos. */
  sin_leer: number;
  /** Se intentó leerlos y no contestaron. Se mide la falta de respuesta, no
   *  la causa: puede ser caída real o un bloqueo por intentos nuestro. */
  no_responden: number;
};

export function ResumenCobertura({
  totales,
  extra,
}: {
  totales: ResumenTotales;
  /** Tarjeta(s) específica(s) del carril, después de las seis estándar. */
  extra?: ReactNode;
}) {
  const t = totales;
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-6">
      <Tarjeta
        titulo="Cobertura"
        valor={`${t.cobertura_pct}%`}
        nota={`${t.leidos} de ${t.total} legibles`}
        alerta={t.cobertura_pct < 80}
      />
      <Tarjeta titulo="Cumplen" valor={t.cumplen} nota={`de ${t.leidos} leídos`} />
      <Tarjeta
        titulo="Revirtieron"
        valor={t.revirtieron}
        nota="estuvieron bien y volvieron atrás"
        alerta={t.revirtieron > 0}
      />
      <Tarjeta
        titulo="Fuera de golden"
        valor={t.fuera}
        nota={`${t.criticos} desvíos críticos`}
        alerta={t.criticos > 0}
      />
      <Tarjeta
        titulo="Sin leer"
        valor={t.sin_leer}
        nota="ni cumplen ni incumplen"
        alerta={t.sin_leer > 0}
      />
      <Tarjeta
        titulo="No responden"
        valor={t.no_responden}
        nota="no contestaron la lectura"
        alerta={t.no_responden > 0}
      />
      {extra}
    </div>
  );
}

/** Un booleano de configuración: sí / no / sin dato. Nunca inventa un valor. */
export function Flag({
  valor,
  malo,
}: {
  valor: boolean | null;
  malo?: boolean;
}) {
  if (valor === null || valor === undefined)
    return <span className="text-muted-foreground">—</span>;
  const enciende = valor === true;
  return (
    <Badge
      variant={
        enciende && malo ? "destructive" : enciende ? "secondary" : "outline"
      }
    >
      {enciende ? "sí" : "no"}
    </Badge>
  );
}

export function Desvios({ filas }: { filas: GrabadorDesvio[] }) {
  if (!filas.length) return <Badge variant="outline">cumple</Badge>;
  return (
    <div className="space-y-1">
      {filas.map((d) => (
        <div key={d.campo} className="text-xs">
          <Badge
            variant={d.critico ? "destructive" : "secondary"}
            className="mr-1"
          >
            {d.campo}
          </Badge>
          <span className="font-mono">{d.actual}</span>
          <span className="text-muted-foreground"> → </span>
          <span className="font-mono">{d.esperado}</span>
          <span className="text-muted-foreground">
            {" "}
            ({ORIGEN[d.origen] ?? d.origen})
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * El golden y su reparto entero.
 *
 * Se muestra el reparto y no sólo el ganador porque la mayoría es un
 * **candidato**, no una verdad: en cámaras ya pasó que la mayoría era la peor
 * configuración y planchar hacia ella habría empeorado las buenas.
 */
export function GoldenReparto({
  golden,
}: {
  golden: Record<string, Record<string, GrabadorCampoGolden>>;
}) {
  return (
    <div className="space-y-3 rounded-md border bg-card p-3">
      <p className="text-xs text-muted-foreground">
        La mayoría es un <strong>candidato</strong>, no una verdad: en cámaras
        ya pasó que la mayoría era la peor configuración. Por eso se muestra el
        reparto entero y no sólo el ganador.
      </p>
      {Object.entries(golden).map(([grupo, campos]) => (
        <div key={grupo}>
          <div className="text-sm font-medium">{grupo}</div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Campo</TableHead>
                  <TableHead>Esperado</TableHead>
                  <TableHead>Sale de</TableHead>
                  <TableHead className="text-right">Cumplen</TableHead>
                  <TableHead className="text-right">Fuera</TableHead>
                  <TableHead>Reparto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(campos).map(([campo, g]) => (
                  <TableRow key={campo}>
                    <TableCell className="text-xs">
                      {campo}
                      {g.critico ? (
                        <Badge variant="destructive" className="ml-1">
                          crítico
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {g.golden}
                    </TableCell>
                    <TableCell className="text-xs">
                      {ORIGEN[g.origen] ?? g.origen}
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      {g.cumplen}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right text-xs",
                        g.fuera > 0 && "text-destructive",
                      )}
                    >
                      {g.fuera}
                    </TableCell>
                    <TableCell className="text-xs">
                      {Object.entries(g.reparto)
                        .map(([v, n]) => `${v} ×${n}`)
                        .join("  ·  ")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}
    </div>
  );
}
